import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import { useFlashMessage } from '../FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';
import DocumentNameSuggestionForm from '../DocumentNaming/DocumentNameSuggestionForm';
import { confirmName } from '../../services/documentNamingService';

// C2 — route a CRM-Hub upload outward into a destination context. Each
// destination reuses an existing, partner-linking backend endpoint so the
// document appears in BOTH the Hub and the destination module's own view.
const DESTINATIONS = [
    { key: 'partner', label: 'This partner only (General Info)', needsContext: false, module: 'partner_info' },
    { key: 'knowledge_hub', label: 'Knowledge Hub', needsContext: false, module: null },
    { key: 'trade_finance', label: 'Trade Finance', needsContext: false, module: 'trade_finance', needsDocType: true },
    { key: 'delivery', label: 'A Delivery', needsContext: true, contextLabel: 'Delivery ID', module: 'delivery', needsDocType: true },
    { key: 'invoice', label: 'An Invoice', needsContext: true, contextLabel: 'Invoice ID', module: 'invoice_document' },
];

const TF_DOC_TYPES = ['BL', 'LC', 'SBLC', 'BG', 'COLLECTION', 'INSURANCE', 'OTHER'];
const DELIVERY_DOC_TYPES = ['Bill of Lading (BL)', 'Certificate of Origin (COO)', 'Certificate of Weight (COW)', 'Commercial Invoice', 'Other'];

const inputStyle = {
    width: '100%', padding: '0.6rem 0.75rem', marginTop: '0.25rem',
    border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '0.875rem',
    backgroundColor: 'white', outline: 'none',
};

const UploadToHubModal = ({ isOpen, onClose, profileId, partnerName, onUploaded }) => {
    const { showError, showSuccess } = useFlashMessage();
    const [destination, setDestination] = useState('partner');
    const [contextId, setContextId] = useState('');
    const [docType, setDocType] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [namingPayload, setNamingPayload] = useState(null);
    const [uploading, setUploading] = useState(false);

    const dest = DESTINATIONS.find((d) => d.key === destination) || DESTINATIONS[0];

    const reset = () => {
        setDestination('partner');
        setContextId('');
        setDocType('');
        setSelectedFile(null);
        setNamingPayload(null);
    };

    const handleCancel = () => {
        if (uploading) return;
        reset();
        onClose();
    };

    // Resolve the create-record endpoint + payload for the chosen destination.
    const buildCreateRequest = () => {
        const subject = (namingPayload?.displayName) || selectedFile?.name || 'Document';
        switch (destination) {
            case 'partner':
                return {
                    url: '/api/docuware/general-partner-info-documents/create-record/',
                    body: { partner_id: profileId, partner_name: partnerName || '', subject },
                    relatedEntityId: String(profileId || ''),
                };
            case 'trade_finance':
                return {
                    url: '/api/docuware/trade-finance/create-record/',
                    body: { partner_id: profileId, partner_name: partnerName || '', document_type: docType || 'OTHER', title: subject, subject },
                    relatedEntityId: String(profileId || ''),
                };
            case 'delivery':
                return {
                    url: '/api/docuware/delivery-documents/create-record/',
                    body: { delivery_id: contextId, delivery_document_type: docType || 'Other', subject },
                    relatedEntityId: String(contextId || ''),
                };
            case 'invoice':
                return {
                    url: '/api/docuware/invoice-documents/create-record/',
                    body: { invoice_id: contextId, supplier_id: profileId, supplier_name: partnerName || '', subject },
                    relatedEntityId: String(contextId || ''),
                };
            default:
                return null;
        }
    };

    const uploadDocuwareFlow = async () => {
        const create = buildCreateRequest();
        // Knowledge Hub uses a single dedicated CRM endpoint (async pipeline).
        if (destination === 'knowledge_hub') {
            const form = new FormData();
            form.append('file', selectedFile);
            form.append('title', (namingPayload?.displayName) || selectedFile.name);
            await axiosInstance.post(`/api/crm/profiles/${profileId}/share-to-knowledge-hub/`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return;
        }

        // Step 1: create the DocuWare record.
        const createRes = await axiosInstance.post(create.url, create.body);
        const documentId = createRes.data?.document_id;
        if (!documentId) throw new Error('No document id returned by create-record.');

        // Step 2: push the file bytes.
        const baseUrl = create.url.replace('create-record/', `upload-file/${documentId}/`);
        const form = new FormData();
        form.append('file', selectedFile);
        await axiosInstance.post(baseUrl, form, { headers: { 'Content-Type': 'multipart/form-data' } });

        // Step 3: persist the AI display name (parity with the other surfaces).
        if (namingPayload?.components && dest.module) {
            try {
                await confirmName({
                    docuwareDocumentId: String(documentId),
                    module: dest.module,
                    file: selectedFile,
                    typeDropdownValue: namingPayload.typeDropdownValue || '',
                    partnerName: namingPayload.partnerName || '',
                    relatedEntityId: create.relatedEntityId,
                    components: namingPayload.components,
                    aiSuggestion: namingPayload.aiSuggestion || {},
                    userOverrides: namingPayload.userOverrides || [],
                    extractionConfidence: namingPayload.extractionConfidence ?? null,
                    originalFilename: selectedFile.name,
                });
            } catch (nameErr) {
                // Naming is best-effort; the upload already succeeded.
                console.warn('confirmName failed for hub upload', nameErr);
            }
        }
    };

    const handleConfirm = async () => {
        if (!selectedFile) {
            showError('Select a file to upload.');
            return;
        }
        if (dest.needsContext && !contextId) {
            showError(`Enter a ${dest.contextLabel} for this destination.`);
            return;
        }
        setUploading(true);
        try {
            await uploadDocuwareFlow();
            showSuccess(`Uploaded "${selectedFile.name}" to ${dest.label}.`);
            reset();
            onClose();
            if (onUploaded) onUploaded();
        } catch (err) {
            console.error('Hub upload failed', err);
            showError(getErrorMessage(err, 'Failed to upload the document.'));
        } finally {
            setUploading(false);
        }
    };

    if (!isOpen) return null;

    // KH handles its own naming; everything else gates on a valid AI name.
    const namingReady = destination === 'knowledge_hub' || !!namingPayload?.isValid;
    const uploadDisabled = uploading || !selectedFile || (dest.needsContext && !contextId) || !namingReady;

    return (
        <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
        }}>
            <div style={{
                backgroundColor: 'white', borderRadius: '12px', padding: '2rem',
                minWidth: '520px', maxWidth: '620px', maxHeight: '85vh', overflow: 'auto',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: 0, color: '#4a6b25', fontSize: '1.2rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faCloudArrowUp} /> Upload to Document Hub
                    </h3>
                    <button onClick={handleCancel} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
                </div>

                <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ fontWeight: 500, color: '#374151' }}>Destination</label>
                    <select value={destination} onChange={(e) => { setDestination(e.target.value); setDocType(''); setContextId(''); }} style={inputStyle} disabled={uploading}>
                        {DESTINATIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                    </select>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.35rem' }}>
                        The document is linked to <strong>{partnerName || 'this partner'}</strong> and also appears in the destination module.
                    </div>
                </div>

                {dest.needsContext && (
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontWeight: 500, color: '#374151' }}>{dest.contextLabel} <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="number" value={contextId} onChange={(e) => setContextId(e.target.value)} placeholder={`Enter ${dest.contextLabel}`} style={inputStyle} disabled={uploading} />
                    </div>
                )}

                {dest.needsDocType && (
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontWeight: 500, color: '#374151' }}>Document Type</label>
                        <select value={docType} onChange={(e) => setDocType(e.target.value)} style={inputStyle} disabled={uploading}>
                            <option value="">-- Select type --</option>
                            {(destination === 'trade_finance' ? TF_DOC_TYPES : DELIVERY_DOC_TYPES).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                )}

                <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ fontWeight: 500, color: '#374151' }}>File <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="file" onChange={(e) => setSelectedFile(e.target.files[0] || null)} style={inputStyle} disabled={uploading}
                        accept=".pdf,.docx,.xlsx,.pptx,.md,.html,.csv,.png,.jpg,.jpeg,.tiff,.bmp,.webp,.txt,.eml,.msg" />
                </div>

                {selectedFile && destination !== 'knowledge_hub' && dest.module && (
                    <div style={{ marginBottom: '1.25rem' }}>
                        <DocumentNameSuggestionForm
                            autoRun
                            file={selectedFile}
                            module={dest.module}
                            relatedEntityId={(dest.needsContext ? contextId : profileId) || ''}
                            typeDropdownValue={dest.needsDocType ? docType : ''}
                            partnerName={partnerName || ''}
                            onChange={setNamingPayload}
                            disabled={uploading}
                        />
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <button onClick={handleCancel} disabled={uploading} style={{ background: 'transparent', border: '1px solid #d1d5db', color: '#374151', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 600, cursor: uploading ? 'not-allowed' : 'pointer' }}>Cancel</button>
                    <button onClick={handleConfirm} disabled={uploadDisabled} style={{ background: uploadDisabled ? '#6c757d' : 'linear-gradient(135deg, #5a7b35, #4a6b25)', color: 'white', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 600, cursor: uploadDisabled ? 'not-allowed' : 'pointer', opacity: uploadDisabled ? 0.7 : 1 }}>
                        {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadToHubModal;
